import { DOCUMENT, InjectionToken, inject } from '@angular/core';
import { Theme, isTheme } from './theme.types';

/**
 * Persists the user's theme choice. Only ThemeService talks to the store, so the
 * storage mechanism can be replaced by providing another implementation for
 * THEME_PREFERENCE_STORE.
 */
export interface ThemePreferenceStore {
  read(): Theme | null;
  write(theme: Theme): void;
}

/**
 * Cookie name. Mirrored by the pre-bootstrap script in src/index.html, which applies
 * the stored theme before the first paint — keep both in sync.
 */
export const THEME_COOKIE_NAME = 'an-theme';

const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

/**
 * First-party cookie store. A cookie is used (rather than localStorage) because the
 * application clears localStorage on login and logout.
 */
export class CookieThemePreferenceStore implements ThemePreferenceStore {
  constructor(private readonly document: Document) {}

  read(): Theme | null {
    const match = this.document.cookie.match(new RegExp(`(?:^|;\\s*)${THEME_COOKIE_NAME}=([^;]*)`));
    const value = match ? decodeURIComponent(match[1]) : null;
    return isTheme(value) ? value : null;
  }

  write(theme: Theme): void {
    const secure = this.document.location?.protocol === 'https:' ? '; Secure' : '';
    this.document.cookie =
      `${THEME_COOKIE_NAME}=${encodeURIComponent(theme)}; path=/; max-age=${ONE_YEAR_IN_SECONDS}; SameSite=Lax${secure}`;
  }
}

export const THEME_PREFERENCE_STORE = new InjectionToken<ThemePreferenceStore>('THEME_PREFERENCE_STORE', {
  providedIn: 'root',
  factory: () => new CookieThemePreferenceStore(inject(DOCUMENT)),
});
